<template>
  <div class="container">
    <CampaignPageHeader
      :campaign="currentCampaign"
      :campaign-record-type="currentCampaignRecordType"
      :loading="loading"
      :mode="mode"
      @activate="handleActivate"
      @cancel="cancel"
      @openDataExtension="openDataExtension"
      @openEditMode="openEditMode"
      @save="save"
      @update="update"
      class="slds-m-bottom--x-small"
    />
    <!-- Wizard -->
    <Card additional-classes="slds-m-bottom--small" v-if="currentWizard && Object.keys(currentWizard).length > 1">
      <div class="slds-path slds-m-top--small">
        <div class="row">
          <button
            class="slds-button slds-button_neutral"
            @click="goToPreviousStep"
            :disabled="isBackButtonDisabled"
          >{{ $t('action.back') }}</button>
          <button
            class="slds-button slds-button_neutral"
            @click="goToNextStep"
            :disabled="isNextButtonDisabled"
          >{{ $t('action.next') }}</button>
        </div>
        <div class="slds-grid slds-path__track slds-m-top--small">
          <div class="slds-grid slds-path__scroller-container">
            <div class="slds-path__scroller" role="application">
              <div class="slds-path__scroller_inner">
                <ul class="slds-path__nav" role="listbox" aria-orientation="horizontal">
                  <li
                    class="slds-path__item"
                    :class="getClassByStatus(tempStep)"
                    role="presentation"
                    v-for="(tempStep, index) in Object.keys(currentWizard)"
                    :key="currentWizard[tempStep].stepNumber"
                  >
                    <a
                      :aria-selected="localStep === localStep.stepNumber"
                      :id="'path-' + index"
                      class="slds-path__link"
                      role="option"
                      tabindex="-1"
                    >
                      <span class="slds-path__stage">
                        <svg class="slds-icon slds-icon_x-small" aria-hidden="true">
                          <use xlink:href="../assets/symbols.svg#check" />
                        </svg>
                        <span class="slds-assistive-text">{{$t('wizard.step.completed')}}</span>
                      </span>
                      <span class="slds-path__title">{{ $t(currentWizard[tempStep].label) }}</span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>

    <!-- Wizard step body -->
    <transition :name="currentTransition" mode="out-in">
      <keep-alive>
        <component
          v-if="currentWizard && localStep"
          class="content"
          ref="step"
          :campaign-status="currentCampaign.status"
          :config="config"
          :is="currentWizard[localStep].component"
          :loading="loading"
          :mode="mode"
          @callFunction="handleCallFunction"
          @changeMode="changeMode"
          @startLoading="loading = true"
          @endLoading="loading = false" />
      </keep-alive>
    </transition>

    <transition name="fade">
      <Modal
        @close="showActivationModal = false"
        @neutralButtonClick="showActivationModal = false"
        :neutralButtonText="$t('action.cancel')"
        @positiveButtonClick="activate"
        :positiveButtonText="$t('general.ok')"
        :title="activationModalTitle"
        v-if="showActivationModal">
        <div class="slds-text-heading_small slds-text-align_center">
          {{activationModalText}}
        </div>
      </Modal>
    </transition>
  </div>
</template>
<script>
import _ from 'lodash'
import { headerCampaign, fullCampaign, salesCampaign, salesFullCampaign, technicalCampaign } from '../config/wizards'
import { getErrorMessage } from '../components/utils/utils'
import { getEmptyConfigByKey } from '../components/utils/indexedDbUtils'
import Card from '../components/slds/Card'
import CampaignPageHeader from '../components/campaign-manager/CampaignPageHeader'
import CheckPermission from '../components/utils/CheckPermission'
import HasMode from '../components/utils/HasMode'
import Modal from '../components/Modal'

const NO_WARNING_CAMPAIGN_TYPES = [
  'sl_sales'
]

const CUSTOM_ACTIVATION_WARNING_CAMPAIGN_TYPES = [
  'as_app_delivery',
  'as_rfid',
  'sr_rfid',
  'sr_app_delivery'
]

export default {
  mixins: [CheckPermission, HasMode],
  components: {
    Card,
    CampaignPageHeader,
    Modal
  },
  props: {
    campaignId: {
      type: String
    },
    step: {
      type: String,
      default: 'campaignData'
    }
  },
  computed: {
    activationModalTitle () {
      if (CUSTOM_ACTIVATION_WARNING_CAMPAIGN_TYPES.includes(this.config.campaignData.type.selected.code)) {
        return this.$t(`campaignDesigner.confirmActivation.${this.config.campaignData.type.selected.code}.title`)
      } else {
        return this.$t(`campaignDesigner.confirmActivation.standard.title`)
      }
    },
    activationModalText () {
      if (CUSTOM_ACTIVATION_WARNING_CAMPAIGN_TYPES.includes(this.config.campaignData.type.selected.code)) {
        return this.$t(`campaignDesigner.confirmActivation.${this.config.campaignData.type.selected.code}.text`)
      } else {
        return this.$t(`campaignDesigner.confirmActivation.standard.text`)
      }
    },
    isBackButtonDisabled () {
      return (this.localStep && this.currentWizard[this.localStep].stepNumber === 0) || this.loading
    },
    previewReady () {
      return this.localStep && this.currentWizard[this.localStep].stepNumber === Object.keys(this.currentWizard).length - 1 && !!this.$refs.step.previewData
    },
    isNextButtonDisabled () {
      return (this.localStep && this.currentWizard[this.localStep].stepNumber === Object.keys(this.currentWizard).length - 1) || this.loading
    }
  },
  data () {
    return {
      config: {},
      currentCampaign: {},
      currentCampaignRecordType: '',
      currentTransition: 'slide-fade-right',
      currentWizard: {},
      loading: false,
      localStep: '',
      mode: '',
      oldState: '',
      showActivationModal: false
    }
  },
  methods: {
    getWizard (type) {
      switch (type) {
        case 'full':
          return fullCampaign
        case 'header':
          return headerCampaign
        case 'sales':
          return salesCampaign
        case 'technical':
          return technicalCampaign
        case 'salesFull':
          return salesFullCampaign
        default:
          throw new Error(`Unresolved campaign type ${type}`)
      }
    },
    async activate () {
      try {
        this.showActivationModal = false
        this.loading = true
        let campaignId
        if (this.isCreate) {
          campaignId = (await this.$store.dispatch('campaignDesigner/save', { recordType: this.currentCampaignRecordType })).id
        } else if (this.isEdit) {
          campaignId = this.currentCampaign.id
          await this.$store.dispatch('campaignDesigner/update', { campaignId, recordType: this.currentCampaignRecordType })
        } else {
          campaignId = this.currentCampaign.id
        }
        const campaign = await this.$store.dispatch('campaignDesigner/activateCampaign', { campaignId, vm: this })
        this.currentCampaign = campaign
        this.loading = false
        this.mode = 'view'
        this.$router.push('/campaigns')
        this.showToast('success', this.$t('general.success'), 'text', this.$t('message.activate'))
      } catch (error) {
        console.error(error)
        this.$router.push('/campaigns')
        this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
      }
    },
    cancel () {
      this.$store.dispatch('campaignDesigner/loadOldState')
      this.mode = 'view'
      this.oldState = ''
      this.$refs.step.callLoadFromStore()
    },
    changeMode (event) {
      this.mode = event.mode
    },
    async handleActivate () {
      try {
        if (!this.$refs.step.validate || this.$refs.step.validate()) {
          if (this.$refs.step.getSectionToSave) {
            const object = this.$refs.step.getSectionToSave()
            await this.$store.dispatch('campaignDesigner/updateCampaignSection', { data: object, section: this.localStep, vm: this, recordType: this.currentCampaignRecordType })

            const userDisabledOrchestration = this.currentCampaign.disableOrchestration
            if (NO_WARNING_CAMPAIGN_TYPES.includes(this.config.campaignData.type.selected.code)) {
              this.activate()
            } else {
              if (CUSTOM_ACTIVATION_WARNING_CAMPAIGN_TYPES.includes(this.config.campaignData.type.selected.code) || !userDisabledOrchestration) {
                this.showActivationModal = true
              } else {
                this.activate()
              }
            }
          }
        } else {
          this.showToast('error', this.$t('validation.invalidData'), 'text', this.$t('validation.invalidDataMessage'))
        }
      } catch (error) {
        console.error(error)
        this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
      }
    },
    async handleCallFunction (event) {
      return new Promise(async (resolve, reject) => {
        const { functionToCall, callback } = event
        try {
          const response = await this[functionToCall]()
          await callback(response)
          resolve()
        } catch (error) {
          reject(error)
        }
      })
    },
    async goToPreviousStep () {
      if ((this.isCreate || this.isEdit) && this.$refs.step.getSectionToSave) {
        await this.$store.dispatch('campaignDesigner/updateCampaignSection', { data: this.$refs.step.getSectionToSave(), section: this.localStep, vm: this, recordType: this.currentCampaignRecordType })
      }
      const currentStepNumber = this.currentWizard[this.localStep].stepNumber
      if (currentStepNumber !== 0) {
        this.currentTransition = 'slide-fade-right'
        this.localStep = Object.keys(this.currentWizard).find(key => this.currentWizard[key].stepNumber === currentStepNumber - 1)
        if (!this.isCreate) {
          this.$router.push(`/campaigns/${this.currentCampaign.id}/${this.localStep}`)
        }
      }
    },
    async goToNextStep () {
      if (this.isView && this.$refs.step.hasCustomValidation) {
        this.mode = 'edit'

        this.$nextTick(async () => {
          await this.goToNextStep()

          this.$nextTick(() => {
            this.mode = 'view'
          })
        })
        return
      }
      if (this.$refs.step.validate) {
        const isValidStep = this.$refs.step.validate()
        if (isValidStep !== undefined) {
          if (isValidStep) {
            if (this.isCreate || this.isEdit) {
              const object = this.$refs.step.getSectionToSave()
              await this.$store.dispatch('campaignDesigner/updateCampaignSection', { data: object, section: this.localStep, vm: this, recordType: this.currentCampaignRecordType })
            }
            const currentStepNumber = this.currentWizard[this.localStep].stepNumber
            if (currentStepNumber + 1 !== Object.keys(this.currentWizard).length) {
              this.currentTransition = 'slide-fade-left'
              this.localStep = Object.keys(this.currentWizard).find(key => this.currentWizard[key].stepNumber === currentStepNumber + 1)
              if (!this.isCreate) {
                this.$router.push(`/campaigns/${this.currentCampaign.id}/${this.localStep}`)
              }
            }
          } else {
            this.showToast('error', this.$t('validation.invalidData'), 'text', this.$t('validation.invalidDataMessage'))
          }
        }
      }
    },
    getClassByStatus (step) {
      if (this.localStep) {
        const currentStepNumber = this.currentWizard[this.localStep].stepNumber
        const targetStepNumber = this.currentWizard[step].stepNumber
        if (currentStepNumber < targetStepNumber) {
          return 'slds-is-incomplete'
        } else if (currentStepNumber === targetStepNumber) {
          return 'slds-is-active'
        } else {
          return 'slds-is-complete'
        }
      } else {
        return 'slds-is-incomplete'
      }
    },
    openDataExtension () {
      open(`${process.env.VUE_APP_MC_INSTANCE_BASE_PATH}/contactsmeta/admin.html#admin/data-extension/${this.currentCampaign.sfmcDataExtensionId}/properties/`)
    },
    openEditMode () {
      this.oldState = JSON.stringify(this.$store.getters['campaignDesigner/campaignDesignerData'])
      this.$store.dispatch('campaignDesigner/saveOldState')
      this.mode = 'edit'
    },
    async save () {
      try {
        if (this.$refs.step.validate) {
          const isValidStep = this.$refs.step.validate()
          if (isValidStep !== undefined) {
            if (isValidStep) {
              this.loading = true
              if (this.$refs.step.getSectionToSave) {
                const object = this.$refs.step.getSectionToSave()
                await this.$store.dispatch('campaignDesigner/updateCampaignSection', { data: object, section: this.localStep, vm: this, recordType: this.currentCampaignRecordType })
              }
              const record = await this.$store.dispatch('campaignDesigner/save', { recordType: this.currentCampaignRecordType })
              this.showToast('success', this.$t('general.success'), 'text', this.$t('message.create'))
              this.currentCampaign = record
              this.mode = 'view'
              this.$router.push(`/campaigns/${record.id}/${this.localStep}`)
              this.currentCampaign.id = record.id
              this.loading = false
              return record.id
            } else {
              this.showToast('error', this.$t('validation.invalidData'), 'text', this.$t('validation.invalidDataMessage'))
            }
          }
        }
      } catch (error) {
        console.error(error)
        this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
      }
    },
    async update () {
      try {
        if (this.$refs.step.validate) {
          const isValidStep = this.$refs.step.validate()
          if (isValidStep !== undefined) {
            if (isValidStep) {
              this.loading = true
              const object = this.$refs.step.getSectionToSave ? this.$refs.step.getSectionToSave() : {}
              await this.$store.dispatch('campaignDesigner/updateCampaignSection', { data: object, section: this.localStep, vm: this, recordType: this.currentCampaignRecordType })
              const record = (await this.$store.dispatch('campaignDesigner/update', { campaignId: this.currentCampaign.id, recordType: this.currentCampaignRecordType }))
              this.showToast('success', this.$t('general.success'), 'text', this.$t('message.update'))
              this.mode = 'view'
              this.loading = false
              this.currentCampaign = record
              return this.currentCampaign.id
            } else {
              this.showToast('error', this.$t('validation.invalidData'), 'text', this.$t('validation.invalidDataMessage'))
            }
          }
        }
      } catch (error) {
        console.error(error)
        this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
      }
    }
  },
  async created () {
    try {
      let mode = ''
      if (this.campaignId) {
        mode = 'view'
        const campaign = await this.$store.dispatch('campaignDesigner/getCampaign', this.campaignId)
        if (campaign.httpCode && campaign.httpCode === '404') {
          this.$router.push('/campaign-not-found')
          return
        } else {
          this.currentCampaign = campaign
          this.currentCampaignRecordType = campaign.recordType
          this.config = _.cloneDeep(campaign.config)
          this.currentWizard = this.getWizard(campaign.recordType)
        }
      } else if (this.$route.fullPath.includes('/new')) {
        const { type } = this.$route.query
        this.currentWizard = this.getWizard(type)
        mode = 'create'
        const config = (await getEmptyConfigByKey(type.toUpperCase())).config
        this.currentCampaign.status = 'Draft'
        this.currentCampaignRecordType = type
        await this.$store.dispatch('campaignDesigner/loadConfig', config)
        this.config = config
      }
      this.mode = mode
      if (this.step) {
        if (Object.keys(this.currentWizard).includes(this.step)) {
          this.localStep = this.step
        } else {
          this.$router.push(`/sectionNotFound/${this.campaignId}`)
          return
        }
      } else {
        this.localStep = Object.keys(this.currentWizard)[0]
      }
      if (!this.isCreate && !this.$router.currentRoute.path.includes(this.localStep)) {
        this.$router.push(`/campaigns/${this.campaignId}/${this.localStep}`)
      }
    } catch (error) {
      console.error(error)
      this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
    }
  },
  beforeRouteLeave (to, from, next) {
    if (this.isCreate || this.isEdit) {
      const confirmation = confirm(this.$t('campaignDesigner.confirmPageLeaveText'))
      if (confirmation) {
        next()
      } else {
        next(false)
      }
    } else {
      next()
    }
  },
  mounted () {
    if (this.$refs.step && this.$refs.step.callLoadFromStore) {
      this.$refs.step.callLoadFromStore()
    }
  },
  beforeDestroy () {
    this.config = {}
    this.currentCampaign = {}
    this.currentTransition = 'slide-fade-right'
    this.currentWizard = {}
    this.loading = false
    this.mode = ''
    this.localStep = ''
    this.oldState = ''
  }
}
</script>
<style lang="scss" scoped>
.container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
}

a.current {
  cursor: default;
  color: #000000;
}

.content {
  max-height: 100%;
}

.slds-media__body {
  margin: auto !important;
}
</style>
